import { z } from 'zod'

import { freeSubscription } from './subscription.db'
import {
  UserSubscriptionTier,
  UserRole,
  SubscriptionEntitlement,
} from './userEnums'
import { ModelUtils } from './utils'
import type { FirestoreDataConverter, Timestamp } from 'firebase/firestore'
import type { FirestoreDataConverter as AdminFirestoreDataConverter } from 'firebase-admin/firestore'
import type { Timestamp as AdminTimestamp } from 'firebase-admin/firestore'

import { PaddleSubscriptionFactory } from './paddleSubscription.db'

export class UserModelFactory {
  public UserActiveSubscriptionDBModel
  public UserDBModel
  public FirestoreUserDBModel
  public NewUserDBModel
  public newUserDBModel
  public userConverter: FirestoreDataConverter<
    z.infer<typeof this.UserDBModel>,
    z.infer<typeof this.FirestoreUserDBModel>
  >
  public adminUserConverter: AdminFirestoreDataConverter<
    z.infer<typeof this.UserDBModel>,
    z.infer<typeof this.FirestoreUserDBModel>
  >
  public userBaseLocale
  public ActiveSubscriptionDBModel
  private utils

  constructor(timestamp: typeof Timestamp | typeof AdminTimestamp) {
    const { PaddleSubscription, FirestorePaddleSubscription } =
      new PaddleSubscriptionFactory(timestamp)
    this.utils = new ModelUtils(timestamp)
    this.UserActiveSubscriptionDBModel = z.object({
      /// The SubscriptionDBModel id associated with this active subscription
      subscription_id: z.string(),
      /// When the subscription will expire.
      /// datetime in UTC milliseconds (includes Grace Period)

      expiration_date_ms: z.number().optional(),
      /// Whether the subscription will auto-renew.
      auto_renewal: z.boolean(),
      /// Auto-renew will switch to this next subscription_id
      auto_renew_subscription_id: z.string().optional(),
      /// Associated apple transaction with this active subscription.
      /// AppleTransactionReceiptDBModel id
      apple_original_transaction_id: z.string().optional(),
    })

    this.ActiveSubscriptionDBModel = z.object({
      tier: z.nativeEnum(UserSubscriptionTier),
      end_date: this.utils.dateForFirestore,
      recurring: z.boolean().default(true),
    })

    this.UserDBModel = z.object({
      id: z.string(),
      /// User Role that user can be in the system.
      role: z.nativeEnum(UserRole),
      /// Date of when the user last accepted the terms of service.
      accepted_tos: this.utils.date.optional(),
      /// the date when the user's free trial ends
      free_trial_end: this.utils.date.optional(),
      /// the date when a user's paid subscription ends
      subscription_end: this.utils.date.optional(),
      paddle_subscriptions: PaddleSubscription.array().optional(),
      /// id for the customer from the Paddle payments system
      paddle_customer_id: z.string().optional(),
      /// Enterprise tier is "enterprise"
      tier: z.nativeEnum(UserSubscriptionTier),
      /// The date of when the account status should be checked next.
      next_check: this.utils.date.optional(),
      /// Available quota to this user (might be overridden due to promo, or for development)
      storage_quota: z.number().default(freeSubscription.storage_quota),
      /// Total storage used by this user.
      storage_used: z.number().default(0),
      storage_reserved: z.number().nonnegative().optional(),
      /// Available distribution keys quota for this user.
      distribution_key_quota: z.number().default(1),
      /// Mark the user and their data for full removal
      tombstoned: z.boolean().default(false),
      /// The associated user's name at time of creation.
      /// This is for reference purpose only.  Truth is in Auth.
      name: z.string().optional(),
      locale: z.string().optional(),
      /// The associated user's email address at time of creation.
      /// This is for reference purpose only.  Truth is in Auth.
      email: z.string().optional(),
      /// Configure app to hide signout, allowing enterprise safe usage.
      app_hide_signout: z.boolean().optional(),
      /// XMeetId
      xmeet_id: z.string().optional(),
      /// Entitlements that user has been granted.
      entitlements: z
        .array(z.nativeEnum(SubscriptionEntitlement))
        .default(freeSubscription.entitlements),
      /// List of active subscriptions that are applied to the account.
      active_subscriptions: z
        .array(this.UserActiveSubscriptionDBModel)
        .optional(),
    })

    this.FirestoreUserDBModel = this.UserDBModel.merge(
      z.object({
        accepted_tos: this.utils.dateForFirestore.optional(),
        next_check: this.utils.dateForFirestore.optional(),
        paddle_subscriptions: FirestorePaddleSubscription.array(),
      }),
    )

    this.NewUserDBModel = this.UserDBModel.omit({
      id: true,
    })
    type NewUserDBModel = z.infer<typeof this.NewUserDBModel>

    type UserDBModel = z.infer<typeof this.UserDBModel>

    this.newUserDBModel = (
      displayName?: string | null,
      email?: string,
    ): NewUserDBModel => {
      const newUser = this.NewUserDBModel.parse({
        role: UserRole.Monocolist,
        tier: UserSubscriptionTier.Free,
        //new free trial ends in 30 days
        free_trial_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        paddle_subscriptions: [],
        name: displayName ? displayName : undefined,
        email: email,
        xmeet_id: '',
        tombstoned: false,
        locale: 'en-US', //TODO - get real locale
      })

      if (newUser.name === null || newUser.name === undefined) {
        delete newUser.name
      }

      return newUser
    }
    this.userConverter = {
      toFirestore: (user: UserDBModel) => {
        return this.FirestoreUserDBModel.parse(user)
      },
      fromFirestore: (snapshot, options) => {
        const data = snapshot.data(options)
        const returnData = this.UserDBModel.parse({ ...data, id: snapshot.id })
        return returnData
      },
    }
    this.adminUserConverter = {
      toFirestore: (user: UserDBModel) => {
        return this.FirestoreUserDBModel.parse(user)
      },
      fromFirestore: (snapshot) => {
        const data = snapshot.data()
        const returnData = this.UserDBModel.parse({ ...data, id: snapshot.id })
        return returnData
      },
    }

    this.userBaseLocale = function (userData: UserDBModel) {
      let locale
      if (userData.locale === undefined || userData.locale === '') {
        locale = 'en-US'
      } else {
        locale = userData.locale
      }
      return locale.split('-')[0] ?? 'en'
    }
  }

  //TODO: this function is outdated

  static canAccess(
    now: Date,
    user: z.infer<typeof UserModelFactory.prototype.UserDBModel>,
  ) {
    if (user.tombstoned) {
      return false
    } else if (user.tier === UserSubscriptionTier.Enterprise) {
      return true
    } else if (user.role === UserRole.Banned) {
      return false
    } else if (UserModelFactory.isFreeTrialActive(user, now)) {
      return true
    } else if (UserModelFactory.hasValidPaidSubscription(user, now)) {
      return true
    }
    return false
  }

  //TODO: this function is outdated
  static isLapsed(
    now: Date,
    user: z.infer<typeof UserModelFactory.prototype.UserDBModel>,
  ) {
    if (user.tier === UserSubscriptionTier.Free) {
      return false
    } else if (user.tier === UserSubscriptionTier.Paid) {
      if (user.subscription_end && user.subscription_end < now) {
        return true
      } else {
        return false
      }
    } else {
      return false
    }
  }
  /**
   * Check if a user is near (but not over) their storage quota (90% threshold)
   * @param user - The user object to check
   * @returns true if the user has used 90% or more of their storage quota
   */
  static isNearQuota(
    user: z.infer<typeof UserModelFactory.prototype.UserDBModel>,
  ): boolean {
    if (this.isOverQuota(user)) {
      return false //
    } else if (user.storage_quota === 0) {
      return false // Avoid division by zero
    }
    const threshold = 0.9 // 90% threshold
    return user.storage_used >= user.storage_quota * threshold
  }

  /**
   * Check if a user is over their storage quota
   * @param user - The user object to check
   * @returns true if the user has exceeded their storage quota
   */
  static isOverQuota(
    user: z.infer<typeof UserModelFactory.prototype.UserDBModel>,
  ): boolean {
    return user.storage_used > user.storage_quota
  }

  /**
   * Check if a user's free trial is nearly over (within 7 days)
   * @param user - The user object to check
   * @param now - The current date
   * @returns true if the free trial is nearly over
   */

  static isFreeTrialNearlyOver(
    user: z.infer<typeof UserModelFactory.prototype.UserDBModel>,
    now: Date,
  ): boolean {
    if (!user.free_trial_end) {
      return false
    }
    const DAYS_IN_MILLISECONDS = 24 * 60 * 60 * 1000
    const threshold = 7 * DAYS_IN_MILLISECONDS
    return user.free_trial_end.getTime() - now.getTime() <= threshold
  }

  static isFreeTrialActive(
    user: z.infer<typeof UserModelFactory.prototype.UserDBModel>,
    now: Date,
  ): boolean {
    if (!user.free_trial_end) {
      return false
    }
    return !this.isFreeTrialOver(user, now)
  }

  static isFreeTrialOver(
    user: z.infer<typeof UserModelFactory.prototype.UserDBModel>,
    now: Date,
  ): boolean {
    if (!user.free_trial_end) {
      return false
    }
    return user.free_trial_end.getTime() < now.getTime()
  }

  static hasValidPaidSubscription(
    user: z.infer<typeof UserModelFactory.prototype.UserDBModel>,
    now: Date,
  ): boolean {
    if (
      user.paddle_subscriptions === undefined ||
      user.paddle_subscriptions.length === 0
    ) {
      return false
    } else {
      const endDate = PaddleSubscriptionFactory.subscriptionEndDate(
        user.paddle_subscriptions,
      )
      if (endDate === null) {
        return false
      } else if (endDate.getTime() < now.getTime()) {
        return false
      }
      return true
    }
  }
}
